
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {LogLevelType} from './LogLevelType';
import {FieldModelLogLevelType} from './LogLevelType';

/**
 * LoggingSettings struct
 */
class LoggingSettings {
  /**
   * Initialize struct
   * @param {!string=} logPath
   * @param {!string=} logFilenameFormat
   * @param {!LogLevelType=} logLevel
   * @constructor
   */
  constructor (logPath = '../logs', logFilenameFormat = '{UtcDate}/{Name}.log', logLevel = new LogLevelType(LogLevelType.INFO)) {
    this.LogPath = logPath
    this.LogFilenameFormat = logFilenameFormat
    this.LogLevel = logLevel
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LoggingSettings}
   * @param {!LoggingSettings} other Other struct
   * @returns {!LoggingSettings} This struct
   */
  copy (other) {
    if (other.LogPath != null) {
      this.LogPath = other.LogPath
    } else {
      this.LogPath = null
    }
    if (other.LogFilenameFormat != null) {
      this.LogFilenameFormat = other.LogFilenameFormat
    } else {
      this.LogFilenameFormat = null
    }
    if (other.LogLevel != null) {
      this.LogLevel = LogLevelType.fromObject(other.LogLevel)
    } else {
      this.LogLevel = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LoggingSettings}
   * @returns {!LoggingSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LoggingSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LoggingSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LoggingSettings}
   * @param {!LoggingSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LoggingSettings)) {
      throw new TypeError('Instance of LoggingSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LoggingSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      LogPath: ((this.LogPath != null) ? this.LogPath : null),
      LogFilenameFormat: ((this.LogFilenameFormat != null) ? this.LogFilenameFormat : null),
      LogLevel: ((this.LogLevel != null) ? this.LogLevel : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LoggingSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LoggingSettings} other Object value
   * @returns {!LoggingSettings} Created struct
   */
  static fromObject (other) {
    return new LoggingSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LoggingSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LoggingSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LoggingSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6
  }
}

export { LoggingSettings };

/**
 * Fast Binary Encoding LoggingSettings field model
 */
class FieldModelLoggingSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.LogPath = new fbe.FieldModelString(buffer, 4 + 4)
    this.LogFilenameFormat = new fbe.FieldModelString(buffer, this.LogPath.fbeOffset + this.LogPath.fbeSize)
    this.LogLevel = new FieldModelLogLevelType(buffer, this.LogFilenameFormat.fbeOffset + this.LogFilenameFormat.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.LogPath.fbeSize + this.LogFilenameFormat.fbeSize + this.LogLevel.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.LogPath.fbeExtra + this.LogFilenameFormat.fbeExtra + this.LogLevel.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLoggingSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLoggingSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLoggingSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LogPath.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LogPath.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogPath.fbeSize

    if ((fbeCurrentSize + this.LogFilenameFormat.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LogFilenameFormat.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogFilenameFormat.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LogLevel.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLoggingSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLoggingSettings}
   * @param {!LoggingSettings} fbeValue Default value, defaults is new LoggingSettings()
   * @returns {!LoggingSettings} LoggingSettings value
   */
  get (fbeValue = new LoggingSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLoggingSettings}
   * @param {!LoggingSettings} fbeValue LoggingSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LogPath.fbeSize) <= fbeStructSize) {
      fbeValue.LogPath = this.LogPath.get('../logs')
    } else {
      fbeValue.LogPath = '../logs'
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogPath.fbeSize

    if ((fbeCurrentSize + this.LogFilenameFormat.fbeSize) <= fbeStructSize) {
      fbeValue.LogFilenameFormat = this.LogFilenameFormat.get('{UtcDate}/{Name}.log')
    } else {
      fbeValue.LogFilenameFormat = '{UtcDate}/{Name}.log'
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogFilenameFormat.fbeSize

    if ((fbeCurrentSize + this.LogLevel.fbeSize) <= fbeStructSize) {
      fbeValue.LogLevel = this.LogLevel.get(new LogLevelType(LogLevelType.INFO))
    } else {
      fbeValue.LogLevel = new LogLevelType(LogLevelType.INFO)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LogLevel.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLoggingSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLoggingSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLoggingSettings}
   * @param {!LoggingSettings} fbeValue LoggingSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLoggingSettings}
   * @param {LoggingSettings} fbeValue LoggingSettings value
   */
  setFields (fbeValue) {
    this.LogPath.set(fbeValue.LogPath)
    this.LogFilenameFormat.set(fbeValue.LogFilenameFormat)
    this.LogLevel.set(fbeValue.LogLevel)
  }
}

export { FieldModelLoggingSettings };

/**
 * Fast Binary Encoding LoggingSettings model
 */
class LoggingSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLoggingSettings(this.buffer, 4)
  }

  /**
   * Get the LoggingSettings model
   * @this {!LoggingSettingsModel}
   * @returns {!FieldModelLoggingSettings} model LoggingSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LoggingSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LoggingSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LoggingSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LoggingSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLoggingSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LoggingSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LoggingSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LoggingSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LoggingSettingsModel}
   * @param {!LoggingSettings} value LoggingSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LoggingSettingsModel}
   * @param {!LoggingSettings} value LoggingSettings value, defaults is new LoggingSettings()
   * @return {!object} Deserialized LoggingSettings value and its size
   */
  deserialize (value = new LoggingSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LoggingSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LoggingSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LoggingSettingsModel}
   * @param {!number} prev Previous LoggingSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LoggingSettingsModel };
